import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Breadcrumbs, BreadcrumbsItem, PageHeader, PageHeaderContent, PageTitle, Section, SectionHeader } from 'components';
import ConnectedSiteSelector from 'components/SiteSelector/ConnectedSiteSelector';
import { useFeatureFlags } from 'contexts/featureFlags';
import { toRem } from 'design';
import { URL } from 'routes/routeConstants';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { CalculatorRules } from 'store/CalculatorRules';
import * as ActionTypes from 'store/Site/types';
import { IStore } from 'store/types';
import { FeatureFlags, IEmployerSite, PayoutMethodTypes } from 'types';
import { hasSitePermission } from 'utils';
import { SelectSiteGrid } from './components';
import PayoutMethodCard from './components/PayoutMethodCard/PayoutMethodCard';
import { PayoutMethodOption, payoutMethodOptions } from './MakePaymentTypes';

interface SelectSiteProps {
  onPayoutMethodSelect: (method: PayoutMethodTypes) => void;
  onSelectSite: (site: IEmployerSite) => void;
  currentEmployeeSites: IEmployerSite[];
  currentSite: IEmployerSite | null;
}

const ContentWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding-bottom: ${toRem(40)};

  .ineligible-label {
    display: none;
  }
`;

const SelectSite = ({ onPayoutMethodSelect, onSelectSite, currentEmployeeSites, currentSite }: SelectSiteProps) => {
  const featureFlags = useFeatureFlags([FeatureFlags.TIP_CALCULATOR]);
  const isSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.Site.GET_SITE_REQUEST));
  const isCalcRulesLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GET_CALCULATOR_RULES_REQUEST));
  const tipCalculatorEnabled = featureFlags[FeatureFlags.TIP_CALCULATOR]?.enabled && currentSite?.settings?.tipCalculator;

  const handleSiteSelect = useCallback(
    (selected: IEmployerSite) => {
      if (!selected) onSelectSite(undefined);

      const siteId = selected?.id;
      const site = (currentEmployeeSites || []).find((site) => site?.id === siteId);
      onSelectSite(site);
    },
    [currentEmployeeSites, onSelectSite]
  );

  return (
    <>
      <PageHeader>
        <Breadcrumbs>
          <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Payments" to={URL.PaymentsOverview} />
          <BreadcrumbsItem data-cy="make-payments-breadcrumblink" label="Make Payment" />
        </Breadcrumbs>

        <PageHeaderContent>
          <PageTitle title="Select a site & payout method" />
        </PageHeaderContent>
      </PageHeader>

      <Section>
        <ContentWrapper>
          <SectionHeader title="Select Site" dataCy="select-site-header" />
          <ConnectedSiteSelector onSiteSelect={handleSiteSelect} />
        </ContentWrapper>

        <ContentWrapper>
          <SectionHeader title="Select Payout Method" dataCy="select-payout-header" />
          <SelectSiteGrid>
            {payoutMethodOptions.map((method: PayoutMethodOption) => {
              const { type, icon, desc, permission } = method;
              if (type === PayoutMethodTypes.CALCULATOR && !tipCalculatorEnabled) return null;

              return (
                <PayoutMethodCard
                  key={`payout-type-${type}`}
                  title={type}
                  subTitle={desc}
                  icon={icon}
                  isLoading={isSiteLoading || (type === PayoutMethodTypes.CALCULATOR && isCalcRulesLoading)}
                  disabled={!hasSitePermission(currentSite?.permissions, permission)}
                  onClick={() => onPayoutMethodSelect(type)}
                />
              );
            })}
          </SelectSiteGrid>
        </ContentWrapper>
      </Section>
    </>
  );
};

export default SelectSite;
